// What a fit costs, before the player presses INVENT DESIGN (R700).
//
// Two prices hang off one draft form, and the form states both. A third,
// the redraft of one slot (R1194), hangs off each master row:
//
//   - the BUILD price is what a hull off this master costs to lay down —
//     the hull cost times one plus the sum of the module cost shares
//     (`Blueprint.Cost` in server/internal/game/blueprints.go);
//   - the DRAFT price is what the invention job itself costs — a share of
//     the hull cost, plus the hull's own draft charge, plus each module's
//     draft charge (`Player.Invent`, same file);
//   - the REDRAFT price is a smaller share of the hull cost, plus the
//     redraft charge of the one slot (`RedraftCost`, same file).
//
// This is a hand mirror of two server sums, and a hand mirror drifts. Every
// input rides the hello catalog (`ModuleSpec.costShare`, `ModuleSpec.
// draftIsotopes`, `ModuleSpec.redraftIsotopes`, `ShipSpec.draftIsotopes`,
// `Catalog.inventionCostShare`, `Catalog.redraftCostShare`),
// so no table is copied here — only the arithmetic. The cross-language
// golden fixture `server/internal/game/testdata/fit-cost-vectors.json` is
// what stops the arithmetic drifting; `fit.test.ts` drives it.
//
// Three rules the mirror must keep:
//
//   - `Math.trunc`, never `Math.round` and never `toFixed`. Go's
//     `uint32(float64(x) * f)` truncates toward zero, so a price the
//     server takes as 87 must not read 88 here.
//   - Truncate per resource, five times over, never on a total. The
//     server's `scaleRes` does it per field.
//   - Do NOT copy `scaleResUp` (blueprints.go, `math.Ceil`). That is the
//     repair price and it rounds the other way (R660); a share of a cost
//     there must never round down to nothing. This is not that.
//
// The module is pure: it imports no DOM and touches no state.

import type { ModuleSpec, Resources } from '../gen/proto/aquaphobia';

/** One resource, scaled the way the server's `scaleRes` scales it. */
function scale(v: number, f: number): number {
  return Math.trunc(v * f);
}

/** Every resource of one price, scaled by one factor. */
function scaleAll(r: Resources, f: number): Resources {
  return {
    alloy: scale(r.alloy, f),
    biomass: scale(r.biomass, f),
    volatiles: scale(r.volatiles, f),
    isotopes: scale(r.isotopes, f),
    credits: scale(r.credits, f),
  };
}

/**
 * What a hull off this fit costs to build: the hull price plus the modules'
 * share of it (numbers.md §6).
 *
 * A fit with no modules takes the server's own share-0 branch and returns
 * the hull cost untouched — not the hull cost times one. The two agree at
 * every figure this pack carries, but the branch is the server's, so the
 * mirror keeps it.
 *
 * That branch still answers with a COPY. The Go twin returns a value, and
 * `hull` here is the catalog's own object: a caller that wrote to the
 * answer would write into the hello.
 */
export function fitCost(hull: Resources, picks: ModuleSpec[]): Resources {
  let share = 0;
  for (const m of picks) share += m.costShare;
  if (share === 0) return { ...hull };
  return scaleAll(hull, 1 + share);
}

/**
 * What the invention job costs: a share of the hull price, then the hull's
 * own draft charge and each module's draft charge on top of the isotopes.
 *
 * The order is load-bearing. The server truncates the scaled hull price
 * FIRST and adds the whole isotope charges to the truncated figure, so a
 * charge never rides through the truncation.
 */
export function draftCost(hull: Resources, hullDraftIsotopes: number,
  picks: ModuleSpec[], inventionCostShare: number): Resources {
  const out = scaleAll(hull, inventionCostShare);
  out.isotopes += hullDraftIsotopes;
  for (const m of picks) out.isotopes += m.draftIsotopes;
  return out;
}

/**
 * What the redraft of one slot costs (numbers.md §6, R1194): a share of the
 * hull price, then the slot's own redraft charge. The hull's own draft
 * charge is not paid again. The truncation comes first, as in `draftCost`.
 */
export function redraftCost(hull: Resources, slot: ModuleSpec,
  redraftCostShare: number): Resources {
  const out = scaleAll(hull, redraftCostShare);
  out.isotopes += slot.redraftIsotopes;
  return out;
}

/**
 * The band a module's effect lands in once the job rolls it (numbers.md §6).
 *
 * `effectPct` is x10 percent on the wire, so 100 is +10.0%. The roll falls
 * between the two bounds the catalog states. The draw is skewed to the low
 * end (R1194), but the band is still the plain product at each end.
 */
export function bonusRange(effectPct: number, rollMin: number, rollMax: number):
{ low: number; high: number } {
  const pct = effectPct / 10;
  return { low: pct * rollMin, high: pct * rollMax };
}

/**
 * The chance of one grade as the panels print it (numbers.md §6, R1194):
 * a percent with one decimal, and "1 in N" with two significant digits,
 * so 0.0435 reads 4.3% and 1 in 23. The server computes the chance; this
 * only formats it.
 */
export function gradeOdds(chance: number): { pct: string; oneIn: string } {
  if (chance <= 0) return { pct: '0.0', oneIn: '—' };
  return { pct: (chance * 100).toFixed(1), oneIn: String(Number((1 / chance).toPrecision(2))) };
}
